import type {
  GuestMessagingInboxItem,
  GuestMessagingMessage,
  GuestMessagingThreadDetail,
  GuestSearchDocument,
  GuestSearchResponse,
  GuestSupportRequest,
} from './guestApi';

export const GuestSupportRequestStatus = {
  DRAFTED: 'drafted',
  SENT: 'sent',
} as const;
export type GuestSupportRequestStatus =
  (typeof GuestSupportRequestStatus)[keyof typeof GuestSupportRequestStatus];

export const GuestSupportRequestPriority = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;
export type GuestSupportRequestPriority =
  (typeof GuestSupportRequestPriority)[keyof typeof GuestSupportRequestPriority];

export const GuestSupportQueueState = {
  QUEUED: 'queued',
  TRIAGED: 'triaged',
  ASSIGNED: 'assigned',
  WAITING_ON_GUEST: 'waiting_on_guest',
  RESOLVED: 'resolved',
} as const;
export type GuestSupportQueueState =
  (typeof GuestSupportQueueState)[keyof typeof GuestSupportQueueState];

export const GuestSupportResolutionState = {
  OPEN: 'open',
  ACKNOWLEDGED: 'acknowledged',
  IN_PROGRESS: 'in_progress',
  RESOLVED: 'resolved',
} as const;
export type GuestSupportResolutionState =
  (typeof GuestSupportResolutionState)[keyof typeof GuestSupportResolutionState];

export const GuestSupportResolutionEventType = {
  CREATED: 'created',
  QUEUED: 'queued',
  ACKNOWLEDGED: 'acknowledged',
  OWNER_ASSIGNED: 'owner_assigned',
  ESCALATED: 'escalated',
  REPLIED: 'replied',
  RESOLVED: 'resolved',
} as const;
export type GuestSupportResolutionEventType =
  (typeof GuestSupportResolutionEventType)[keyof typeof GuestSupportResolutionEventType];

export const GuestMessagingThreadType = {
  ANNOUNCEMENT: 'announcement',
  GUEST_SUPPORT: 'guest_support',
  GUEST_REPLY: 'guest_reply',
  EVENT_UPDATE: 'event_update',
  REMINDER: 'reminder',
} as const;
export type GuestMessagingThreadType =
  (typeof GuestMessagingThreadType)[keyof typeof GuestMessagingThreadType];

export const GuestMessagingBodyFormat = {
  PLAIN_TEXT: 'plain_text',
  MARKDOWN: 'markdown',
  HTML: 'html',
} as const;
export type GuestMessagingBodyFormat =
  (typeof GuestMessagingBodyFormat)[keyof typeof GuestMessagingBodyFormat];

export const GuestMessagingMessageType = {
  USER: 'user',
  SYSTEM: 'system',
  ANNOUNCEMENT: 'announcement',
  REMINDER: 'reminder',
} as const;
export type GuestMessagingMessageType =
  (typeof GuestMessagingMessageType)[keyof typeof GuestMessagingMessageType];

export const GuestMessagingParticipantKind = {
  PLANNER: 'planner',
  VENDOR: 'vendor',
  GUEST: 'guest',
  SYSTEM: 'system',
} as const;
export type GuestMessagingParticipantKind =
  (typeof GuestMessagingParticipantKind)[keyof typeof GuestMessagingParticipantKind];

export const GuestSearchSurface = {
  VENDOR: 'vendor',
  PLANNER: 'planner',
  GUEST: 'guest',
  ADMIN: 'admin',
  INTELLIGENCE: 'intelligence',
} as const;
export type GuestSearchSurface =
  (typeof GuestSearchSurface)[keyof typeof GuestSearchSurface];

export interface GuestSupportRequestResponse {
  id: string;
  actor_id: string;
  actor_display_name: string;
  actor_type: string;
  distribution_id: string | null;
  invitation_id: string | null;
  question: string;
  route: string;
  status: GuestSupportRequestStatus;
  priority: GuestSupportRequestPriority;
  queue_state: GuestSupportQueueState;
  assigned_owner: string | null;
  escalation_owner: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  sla_due_at: string | null;
  resolution_state: GuestSupportResolutionState;
  replies: Array<{
    id: string;
    body: string;
    author: string;
    sentAt: string;
  }>;
  resolution_events: Array<{
    id: string;
    type: GuestSupportResolutionEventType;
    body: string;
    createdAt: string;
  }>;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
}

export interface GuestMessagingThreadResponse {
  id: string;
  event_id: string | null;
  subject: string | null;
  guest_thread_type: GuestMessagingThreadType;
  guest_reply_allowed: boolean;
  updated_at: string;
  last_message_at: string | null;
}

export interface GuestMessagingMessageResponse {
  id: string;
  body: string;
  body_format: GuestMessagingBodyFormat;
  message_type: GuestMessagingMessageType;
  sender_display_name: string;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  attachment_count: number;
}

export interface GuestMessagingParticipantResponse {
  display_name: string;
  participant_kind: GuestMessagingParticipantKind;
}

export interface GuestMessagingInboxItemResponse {
  thread: GuestMessagingThreadResponse;
  latest_message: GuestMessagingMessageResponse | null;
  participants: GuestMessagingParticipantResponse[];
  unread_count: number;
  last_activity_at: string;
  latest_message_preview: string | null;
}

export interface GuestMessagingThreadDetailResponse {
  thread: GuestMessagingThreadResponse;
  participants: GuestMessagingParticipantResponse[];
  messages: GuestMessagingMessageResponse[];
  page_size: number;
  next_before: string | null;
}

export interface GuestSearchResponseBody {
  items: GuestSearchResponseItemBody[];
  groups: Array<{
    resource_type: string;
    label: string;
    icon: string;
    total: number;
    view_all_path: string | null;
    items: GuestSearchResponseItemBody[];
  }>;
  total: number;
  analytics_id: string | null;
  metadata: {
    latency_ms: number;
    partial: boolean;
    partial_failures: Array<{
      resource_type: string;
      reason: string;
    }>;
    surface: GuestSearchSurface | null;
  };
}

interface GuestSearchResponseItemBody {
  document: GuestSearchDocument;
  score: number | null;
  highlights: string[];
  snippets: Array<{
    field: string;
    text: string;
    highlighted_text: string;
  }>;
}

export function mapGuestSupportRequest(
  request: GuestSupportRequestResponse,
): GuestSupportRequest {
  return GuestApiResponseMapper.mapGuestSupportRequest(request);
}

export function mapGuestMessagingMessage(
  message: GuestMessagingMessageResponse,
): GuestMessagingMessage {
  return GuestApiResponseMapper.mapGuestMessagingMessage(message);
}

export function mapGuestMessagingInboxItem(
  item: GuestMessagingInboxItemResponse,
): GuestMessagingInboxItem {
  return GuestApiResponseMapper.mapGuestMessagingInboxItem(item);
}

export function mapGuestMessagingThreadDetail(
  response: GuestMessagingThreadDetailResponse,
): GuestMessagingThreadDetail {
  return GuestApiResponseMapper.mapGuestMessagingThreadDetail(response);
}

export function mapGuestSearchResponse(
  response: GuestSearchResponseBody,
): GuestSearchResponse {
  return GuestApiResponseMapper.mapGuestSearchResponse(response);
}

export class GuestApiResponseMapper {
  static mapGuestSupportRequest(
    request: GuestSupportRequestResponse,
  ): GuestSupportRequest {
    return {
      id: request.id,
      actorId: request.actor_id,
      actorDisplayName: request.actor_display_name,
      actorType: request.actor_type,
      distributionId: request.distribution_id,
      invitationId: request.invitation_id,
      question: request.question,
      route: request.route,
      status: request.status,
      priority: request.priority,
      queueState: request.queue_state,
      assignedOwner: request.assigned_owner,
      escalationOwner: request.escalation_owner,
      acknowledgedAt: request.acknowledged_at,
      resolvedAt: request.resolved_at,
      slaDueAt: request.sla_due_at,
      resolutionState: request.resolution_state,
      replies: request.replies,
      resolutionEvents: request.resolution_events,
      createdAt: request.created_at,
      updatedAt: request.updated_at,
      metadata: request.metadata,
    };
  }

  static mapGuestMessagingMessage(
    message: GuestMessagingMessageResponse,
  ): GuestMessagingMessage {
    return {
      id: message.id,
      body: message.body,
      bodyFormat: message.body_format,
      messageType: message.message_type,
      senderDisplayName: message.sender_display_name,
      createdAt: message.created_at,
      editedAt: message.edited_at,
      deletedAt: message.deleted_at,
      attachmentCount: message.attachment_count,
    };
  }

  static mapGuestMessagingInboxItem(
    item: GuestMessagingInboxItemResponse,
  ): GuestMessagingInboxItem {
    return {
      thread: this.mapGuestMessagingThread(item.thread),
      latestMessage: item.latest_message
        ? this.mapGuestMessagingMessage(item.latest_message)
        : null,
      participants: item.participants.map((participant) => ({
        displayName: participant.display_name,
        participantKind: participant.participant_kind,
      })),
      unreadCount: item.unread_count,
      lastActivityAt: item.last_activity_at,
      latestMessagePreview: item.latest_message_preview,
    };
  }

  static mapGuestMessagingThreadDetail(
    response: GuestMessagingThreadDetailResponse,
  ): GuestMessagingThreadDetail {
    return {
      thread: this.mapGuestMessagingThread(response.thread),
      participants: response.participants.map((participant) => ({
        displayName: participant.display_name,
        participantKind: participant.participant_kind,
      })),
      messages: response.messages.map((message) =>
        this.mapGuestMessagingMessage(message),
      ),
      pageSize: response.page_size,
      nextBefore: response.next_before,
    };
  }

  static mapGuestSearchResponse(
    response: GuestSearchResponseBody,
  ): GuestSearchResponse {
    const mapSearchItem = (item: GuestSearchResponseItemBody) => ({
      ...item,
      snippets: item.snippets.map((snippet) => ({
        field: snippet.field,
        text: snippet.text,
        highlightedText: snippet.highlighted_text,
      })),
    });

    return {
      items: response.items.map(mapSearchItem),
      groups: response.groups.map((group) => ({
        resourceType: group.resource_type,
        label: group.label,
        icon: group.icon,
        total: group.total,
        viewAllPath: group.view_all_path,
        items: group.items.map(mapSearchItem),
      })),
      total: response.total,
      analyticsId: response.analytics_id,
      metadata: {
        latencyMs: response.metadata.latency_ms,
        partial: response.metadata.partial,
        partialFailures: response.metadata.partial_failures.map((failure) => ({
          resourceType: failure.resource_type,
          reason: failure.reason,
        })),
        surface: response.metadata.surface,
      },
    };
  }

  private static mapGuestMessagingThread(
    thread: GuestMessagingThreadResponse,
  ): GuestMessagingInboxItem['thread'] {
    return {
      id: thread.id,
      eventId: thread.event_id,
      subject: thread.subject,
      guestThreadType: thread.guest_thread_type,
      guestReplyAllowed: thread.guest_reply_allowed,
      updatedAt: thread.updated_at,
      lastMessageAt: thread.last_message_at,
    };
  }
}
